import React from 'react';
import { Breakpoint, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import {
  HyperButtonLink,
  IconCheckmark,
  Modal,
  ModalChildrenProps,
  OutlineButton,
  SolidButtonLink,
} from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { getProductImageURL, priceFormatter } from '@noths/polaris-client-utils';

import { PriceBreakdownContainer } from 'src/components/molecules/PriceBreakdown';
import { RecommendedProductsShelfContainer } from 'src/components/organisms/RecommendedProductsShelf';
import { Placement } from 'src/constants/recommendations';
import { FavouriteProduct } from 'src/redux/selectors';
import { Basket } from 'src/types';
import {
  BASKET_MODAL_BUTTON_CONTINUE,
  BASKET_MODAL_BUTTON_CTA,
  BASKET_MODAL_CONTENT_LABEL,
  BASKET_MODAL_HEADER,
  BASKET_MODAL_ITEMS,
  BASKET_MODAL_QUANTITY,
  BASKET_MODAL_TOTAL,
} from '../constants';
import { useAddToCartPlacement } from '../hooks/useAddToCartPlacement';
import * as styles from '../styles/ProductInfo.styles';

export interface CartModalProps {
  product: FavouriteProduct;
  basket: Basket | null;
  onModalClose: VoidFunction;
  onModalApply: VoidFunction;
  isModalOpen: boolean;
  hideAddToCartPlacement: boolean;
  richRelevanceSessionId?: string;
}

const BasketInfoAndCTAs = ({
  basket,
  onModalApply,
  onModalClose,
}: Pick<CartModalProps, 'basket' | 'onModalApply' | 'onModalClose'>) => {
  return (
    <>
      <div css={styles.basketInfo}>
        {basket?.itemsTotal && (
          <p css={styles.basketInfoTotal}>
            {`${BASKET_MODAL_TOTAL}: ${priceFormatter(
              basket.itemsTotal.currency,
              basket.itemsTotal.cents,
            )}`}
          </p>
        )}
        {Boolean(basket?.itemsCount) && (
          <p css={styles.basketInfoItems}>
            {`${basket!.itemsCount} ${BASKET_MODAL_ITEMS}`}
            <span css={visuallyHidden}> in the basket</span>
          </p>
        )}
      </div>
      <div css={styles.buttonContainer}>
        <OutlineButton fullWidth onClick={onModalClose}>
          {BASKET_MODAL_BUTTON_CONTINUE}
        </OutlineButton>
        <SolidButtonLink
          href="/cart"
          onClick={(e) => {
            e.preventDefault();
            onModalApply();
          }}
          fullWidth
          css={styles.goToBasketButton}
        >
          {BASKET_MODAL_BUTTON_CTA}
        </SolidButtonLink>
      </div>
    </>
  );
};

export const CartModal = ({
  product: { title, partner, quantity, images, code },
  basket,
  onModalApply,
  onModalClose,
  isModalOpen,
  hideAddToCartPlacement,
  richRelevanceSessionId,
}: CartModalProps) => {
  const isOnSmallScreen = useBreakpoint() === Breakpoint.S;

  useAddToCartPlacement(richRelevanceSessionId, code, isModalOpen);

  return (
    <>
      <Modal
        appRootSelector="#NOTHSPageWrapper"
        isModalOpen={isModalOpen}
        contentLabel={BASKET_MODAL_CONTENT_LABEL}
        onModalClose={onModalClose}
      >
        {({ Header, Body, Footer }: ModalChildrenProps) => (
          <>
            <Header onCloseButtonClick={onModalClose} notificationStatus="success">
              <div css={[styles.addToBasketSection, styles.addToBasketHeaderSection]}>
                <IconCheckmark width={24} height={24} />
                <h2 css={styles.addToBasketHeader}>{BASKET_MODAL_HEADER}</h2>
              </div>
            </Header>
            <Body>
              <div css={styles.bodyWrapper}>
                <div css={[styles.addToBasketSection, styles.addToBasketProductSection]}>
                  <div css={styles.partnerInfo}>
                    <img
                      css={styles.partnerImage}
                      alt=""
                      width={135}
                      height={135}
                      src={getProductImageURL(images[0].href, 'mini')}
                    />
                    <div>
                      <HyperButtonLink href={partner.links?.[1]?.rel || ''}>
                        {partner.name}
                      </HyperButtonLink>
                      <p css={styles.productDescription}>{title}</p>
                      <p css={styles.productQuantity}>{`${BASKET_MODAL_QUANTITY}: ${quantity}`}</p>
                    </div>
                  </div>
                  <div css={styles.basketBreakdownWrapper}>
                    <PriceBreakdownContainer showTotalDivider expandablePersonalisations />
                  </div>
                </div>
                <div css={[styles.addToBasketSection, styles.addToBasketButtonSection]}>
                  {!isOnSmallScreen && (
                    <BasketInfoAndCTAs
                      basket={basket}
                      onModalApply={onModalApply}
                      onModalClose={onModalClose}
                    />
                  )}
                </div>
                {!hideAddToCartPlacement && (
                  <div css={styles.addToBasketCarouselContainer}>
                    <div css={styles.addToBasketCarouselSection}>
                      <RecommendedProductsShelfContainer
                        placementType={Placement.AddToCart}
                        size="small"
                        enableFreeDomesticDeliveryLabels={true}
                        hideLabelIcons={true}
                      />
                    </div>
                  </div>
                )}
              </div>
            </Body>
            {isOnSmallScreen && (
              <Footer>
                <div css={styles.footerItems}>
                  <BasketInfoAndCTAs
                    basket={basket}
                    onModalApply={onModalApply}
                    onModalClose={onModalClose}
                  />
                </div>
              </Footer>
            )}
          </>
        )}
      </Modal>
      <styles.AddToBasketModalGlobalStyles />
    </>
  );
};
